/**
 * Applies multiple filters to data dynamically
 * @param data - Array of objects to filter
 * @param filters - Object with field names as keys and arrays of selected values as values
 * @returns Filtered array of objects
 */
export function applyFilters(
	data: any[],
	filters: Record<string, string[]>
): any[] {
	if (!data || data.length === 0) return [];
	if (!filters || Object.keys(filters).length === 0) return data;

	return data.filter((item: any) => {
		// Check each filter field
		for (const [field, selectedValues] of Object.entries(filters)) {
			// Skip if no values are selected for this field
			if (!selectedValues || selectedValues.length === 0) {
				continue;
			}

			// Normalize selected values and treat empty strings as "no filter"
			const normalizedValues = (selectedValues as any[])
				.map((v) => String(v ?? "").trim())
				.filter((v) => v.length > 0);

			// If after normalization there are no active values, skip filtering for this field
			if (normalizedValues.length === 0) {
				continue;
			}

			// Get the item's value for this field
			const itemValue = item[field];

			// Skip if item doesn't have this field
			if (itemValue === undefined || itemValue === null) {
				return false;
			}

			// Convert to string for comparison (case-insensitive)
			const itemValueStr = String(itemValue).toLowerCase().trim();

			// Check if any of the selected values match
			const hasMatch = normalizedValues.some((selectedValue: any) => {
				const fieldValue = String(selectedValue).trim().toLowerCase();
				return itemValueStr.includes(fieldValue);
			});

			if (!hasMatch) {
				return false;
			}
		}

		return true;
	});
}

/**
 * Creates a filter configuration object from data
 * @param data - Array of objects to analyze
 * @param fields - Optional array of specific fields to include in filters
 * @returns Object with field names as keys and empty arrays as values
 */
export function createFilterConfig(
	data: any[],
	fields?: string[]
): { [key: string]: string[] } {
	if (!data || data.length === 0) return {};

	const config: { [key: string]: string[] } = {};

	if (fields) {
		// Use provided fields
		fields.forEach((field) => {
			config[field] = [];
		});
	} else {
		// Auto-detect filterable fields
		const sampleItem = data[0];
		Object.keys(sampleItem).forEach((key) => {
			const value = sampleItem[key];
			if (typeof value === "string" || typeof value === "number") {
				config[key] = [];
			}
		});
	}

	return config;
}
